import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import { API_BASE_URL, getApiToken } from "../config/authConfig";

export default function ReviewSubmit({ travelRequests, behalfUser, onBack, onSubmitted }) {
  const { accounts, instance } = useMsal();
  const user = accounts[0] || {};
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const targetUser = behalfUser
    ? { username: behalfUser.userPrincipalName, name: behalfUser.displayName }
    : { username: user.username, name: user.name };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const token = await getApiToken(instance);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const payload = {
        userUPN: targetUser.username,
        displayName: targetUser.name,
        submittedByUPN: user.username,
        travelRequests: travelRequests.map((r) => ({
          country: r.country,
          countryCode: r.countryCode,
          securityGroupId: r.securityGroupId,
          securityGroup: r.securityGroup,
          startDate: r.startDate,
          endDate: r.endDate,
        })),
      };

      await axios.post(`${API_BASE_URL}/travel/submitTravelRequest`, payload, { headers });
      const refId = `TR-${Date.now()}`;
      onSubmitted(refId);
    } catch (err) {
      setError(err.response?.data?.error || "Submission failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <header className="app-header">
        <div className="header-inner">
          <div className="header-logo">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <rect width="32" height="32" rx="8" fill="#230658" />
              <path d="M9 16L14 21L23 11" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Travel Request Portal</span>
          </div>
          <div className="header-user">
            <div className="user-avatar">{user.name ? user.name[0].toUpperCase() : "U"}</div>
            <span>{user.name}</span>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="page-title-block">
          <h2>Review your request</h2>
          <p>Please confirm the details below before submitting.</p>
        </div>

        <div className="review-card">
          <div className="review-user-block">
            <div className="user-avatar large">{targetUser.name ? targetUser.name[0].toUpperCase() : "U"}</div>
            <div>
              <strong>{targetUser.name}</strong>
              <span className="review-upn">{targetUser.username}</span>
              {behalfUser && (
                <span className="review-upn" style={{ color: "var(--accent)" }}>
                  Submitted by: {user.username}
                </span>
              )}
            </div>
          </div>

          <div className="review-divider" />

          <h3>Travel destinations ({travelRequests.length})</h3>
          <div className="review-list">
            {travelRequests.map((req, i) => (
              <div key={i} className="review-item">
                <div className="review-item-num">{i + 1}</div>
                <div className="review-item-body">
                  <div className="review-country">
                    <div className="trip-code-badge" style={{ width: 30, height: 30, fontSize: 10 }}>{req.countryCode}</div>
                    <strong>{req.country}</strong>
                  </div>
                  <div className="review-dates">
                    <span className="date-badge start">Depart {formatDate(req.startDate)}</span>
                    <span className="date-arrow">→</span>
                    <span className="date-badge end">Return {formatDate(req.endDate)}</span>
                  </div>
                  <div className="review-group">Security group: <code>{req.securityGroup}</code></div>
                </div>
              </div>
            ))}
          </div>

          {error && <div className="error-banner">{error}</div>}

          {submitting && (
            <div className="submitting-note">
              Setting up your travel security policies... please do not close this window.
            </div>
          )}

          <div className="review-actions">
            <button className="btn-secondary" onClick={onBack} disabled={submitting}>← Back</button>
            <button className="btn-primary" onClick={handleSubmit} disabled={submitting}>
              {submitting ? <><span className="btn-spinner" /> Submitting... (this may take up to 2 minutes)</> : "Confirm & Submit"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${m}/${d}/${y}`;
}